import React, { Fragment, useRef } from "react";
import PropTypes from "prop-types";
import uniqueId from "lodash.uniqueid";

export default function TextInputComponent({
  label = null,
  labelClasses = "",
  placeholder,
  onChange,
  onKeyUp,
  onKeyDown,
  onFocus,
  onBlur = () => {},
  value,
  hasIcon,
  type = "text",
  disabled = false,
  maxLength,
  required,
  autoFocus = false,
  startIcon,
  endIcon,
  onIconClick,
  variantClass,
  customClassName,
  max,
  min,
  step,
  name,
  defaultValue = "",
  floatingLabel = false,
  width = "w-full",
  autoComplete = "on",
}) {
  const { current: TEXT_INPUT_ID } = useRef(uniqueId(`text-input-`));
  return (
    <Fragment>
      {label && !floatingLabel ? (
        <label
          className={`${
            labelClasses
              ? labelClasses
              : "block pb-3 font-serif text-sm font-medium md:text-xl"
          }`}
        >
          {label}
        </label>
      ) : null}
      <div className="relative">
        {startIcon && (
          <span className="flex absolute inset-y-0 left-0 items-center pl-2">
            <button
              type="button"
              className="pr-2 text-gray-600 focus:outline-none focus:shadow-outline"
              onClick={onIconClick}
            >
              {startIcon}
            </button>
          </span>
        )}
        {endIcon && (
          <span className="flex absolute inset-y-0 right-2 items-center pl-2">
            <button
              type="button"
              className="pr-1 focus:outline-none text-space-50 focus:shadow-outline"
              onClick={onIconClick}
            >
              {endIcon}
            </button>
          </span>
        )}

        {floatingLabel && (
          <label
            className={`absolute left-0 px-2 text-space-40 transition-top md:px-3 ${
              value
                ? "-top-0.5 text-xs opacity-100"
                : "top-3 text-base opacity-0"
            }`}
            htmlFor={TEXT_INPUT_ID}
          >
            {label}
          </label>
        )}
        <form autoComplete={autoComplete}>
          <input
            className={`focus:outline-none ${width} rounded-md border border-space-30 p-2 font-serif text-gray-700 focus:border-space-200 disabled:cursor-not-allowed disabled:shadow-none sm:rounded-lg md:p-3 ${
              startIcon ? "pl-6 md:pl-6" : ""
            } ${
              endIcon ? "pr-9 md:pr-10" : ""
            } ${variantClass} ${customClassName}`}
            type={type}
            placeholder={placeholder}
            onChange={(event) => {
              if (disabled) return;
              if (min && Number(event.target.value) < Number(min))
                event.target.value = min;
              if (max && Number(event.target.value) > Number(max))
                event.target.value = event.target.value.substring(
                  0,
                  max.length - 1
                );

              onChange(event);
            }}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            disabled={disabled}
            value={value}
            maxLength={maxLength}
            required={required}
            autoFocus={autoFocus}
            max={max}
            min={min}
            step={step}
            name={name ? name : TEXT_INPUT_ID}
            autoComplete={autoComplete}
            defaultValue={defaultValue}
            id={TEXT_INPUT_ID}
            onBlur={onBlur}
          />
        </form>
      </div>
    </Fragment>
  );
}

TextInputComponent.propTypes = {
  /** This sets the type of the input field. Eg: text, email, password */
  type: PropTypes.string,

  /** This sets the label of the input field */
  label: PropTypes.string,

  /** This sets the placeholder of the input field */
  placeholder: PropTypes.string,

  /** This function will run on any change in the input field */
  onChange: PropTypes.func,

  /** This function will run on any key press in the input field */
  onKeyUp: PropTypes.func,

  /** This function will run when the input field is in focus */
  onFocus: PropTypes.func,

  /** This prop disables the input field */
  disabled: PropTypes.bool,

  /** This prop sets the value of the input field */
  value: PropTypes.string,

  /** This prop tell wether input field has a icon with it or not.
   * using which it adjust it's padding*/
  hasIcon: PropTypes.bool,
};
